import type { CurrentStarterInfo } from '@racemap/utilities/api-client';
import type { PointWithOffsetTimeNumber } from '@racemap/utilities/functions/codec';
import type { UnitType } from '@racemap/utilities/types/units';
import { type FC, useRef, useState } from 'react';
import { useElementDimensions } from 'src/lib/player/utils/useElementDimensions';
import { currentAPIColors } from '../consts';
import { AxesChrome, TickLabels } from './ChartAxes';
import { buildChartElements, type ChartElements } from './chartElements';
import { downloadChartSvg } from './downloadChartSvg';
import { gapAnchors, gapSegments } from './gapSegments';
import { chartSize, chartTheme, GC_H, GC_INNER_H, GC_PAD_LEFT, GC_PAD_TOP } from './layout';
import { PlotLayers } from './PlotLayers';
import { buildScales, gapTimes } from './scales';
import { useLabelSprings } from './useLabelSprings';

const NO_ELEMENTS: ChartElements = { dots: [], lines: [], texts: [], rects: [] };

export interface GapsChartProps {
  starterCurrent: CurrentStarterInfo | null;
  leaderCurrent: CurrentStarterInfo | null;
  resultTimeInMillis: number | null;
  rawStarterPoints: Array<PointWithOffsetTimeNumber>;
  rawLeaderPoints: Array<PointWithOffsetTimeNumber>;
  avgSpeedWindowSeconds: number;
  unitType: UnitType;
  isDayMode?: boolean;
  eventName?: string;
}

export const GapsChart: FC<GapsChartProps> = ({
  starterCurrent,
  leaderCurrent,
  resultTimeInMillis,
  rawStarterPoints,
  rawLeaderPoints,
  avgSpeedWindowSeconds,
  unitType,
  isDayMode = false,
  eventName,
}) => {
  const colors = currentAPIColors(isDayMode);
  const theme = chartTheme(isDayMode);

  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [hovered, setHovered] = useState(false);
  const { width: containerWidth, height: containerHeight } = useElementDimensions(containerRef);
  const { width, innerWidth } = chartSize(containerWidth, containerHeight);

  const starter = starterCurrent?.current ?? null;
  const leader = leaderCurrent?.current ?? null;
  const times = gapTimes(starter, leader, resultTimeInMillis);
  const scales = buildScales(starter, leader, times);
  const segments =
    scales != null && starter != null ? gapSegments(starter, leader, times, scales) : {};
  const anchors =
    scales != null && starter != null ? gapAnchors(segments, starter, leader, times, scales) : null;
  const geometry = useLabelSprings(anchors);
  const elements =
    scales != null
      ? buildChartElements({
          scales,
          times,
          starterCurrent,
          leaderCurrent,
          segments,
          anchors,
          geometry,
          rawStarterPoints,
          rawLeaderPoints,
          avgSpeedWindowSeconds,
          unitType,
          colors,
          innerWidth,
        })
      : NO_ELEMENTS;

  const handleDownload = () => {
    if (svgRef.current != null) downloadChartSvg(svgRef.current, width, eventName);
  };

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', width: '100%', height: '100%' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${GC_H}`}
        preserveAspectRatio="xMinYMid meet"
        style={{ width: '100%', height: '100%', display: 'block' }}
        role="img"
        aria-labelledby="gc-title"
      >
        <title id="gc-title">Gaps chart — participant and leader offset over time</title>
        <rect x={0} y={0} width={width} height={GC_H} fill={theme.bgFill} />
        <defs>
          <marker id="gc-arrow" markerWidth="7" markerHeight="5" refX="7" refY="2.5" orient="auto">
            <polygon points="0 0, 7 2.5, 0 5" fill={theme.axisStroke} />
          </marker>
          <clipPath id="gc-plot-area">
            <rect x={GC_PAD_LEFT} y={GC_PAD_TOP} width={innerWidth} height={GC_INNER_H} />
          </clipPath>
        </defs>
        <AxesChrome innerWidth={innerWidth} theme={theme} hasData={scales != null} />
        <PlotLayers elements={elements} innerWidth={innerWidth} />
        {scales != null && (
          <TickLabels innerWidth={innerWidth} theme={theme} scales={scales} unitType={unitType} />
        )}
      </svg>
      {hovered && (
        <button
          type="button"
          onClick={handleDownload}
          title="Download SVG"
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            background: 'rgba(0,0,0,0.45)',
            border: 'none',
            borderRadius: 3,
            color: '#fff',
            cursor: 'pointer',
            fontSize: 11,
            padding: '2px 6px',
            lineHeight: '18px',
          }}
        >
          ↓ SVG
        </button>
      )}
    </div>
  );
};
